import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useGetGroupsQuery, useGetProjectsQuery, useGetProjectsFromGroupQuery } from 'app/platformApi';
import { useForceSyncMutation } from 'app/adminApi';
import { MagnifyingGlassIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import ComboboxAutocomplete from 'components/ComboboxAutocomplete';
import type { IOption } from 'components/ComboboxAutocomplete';
import type { IPlatformProject } from 'utils/platformApiTypes';
import { useNavigate } from 'react-router-dom';
import { classNames } from 'utils/styleUtils';
import { formatRelativeTime } from 'utils/formatUtils';
import {
  parseRepositoryMetadataFromDescription,
  getRepositoryUrl,
  isValidExternalUrl,
} from 'utils/repositorySourceDetection';

/**
 * Component for rendering project path link with proper error handling and accessibility
 */
interface ProjectPathLinkProps {
  project: IPlatformProject;
}

const ProjectPathLink: React.FC<ProjectPathLinkProps> = ({ project }) => {
  const projectPath = project?.namespace?.full_path;

  // Handle missing or invalid data
  if (projectPath === null || projectPath === undefined || projectPath === '') {
    return (
      <span className="text-gray-400 italic" aria-label="Project path unavailable">
        Path unavailable
      </span>
    );
  }

  // Get GitHub URL from description
  const githubUrl = useMemo(() => {
    try {
      const metadata = parseRepositoryMetadataFromDescription(project.description);
      return metadata.githubUrl;
    } catch (error) {
      console.warn('Failed to parse GitHub URL for project path link:', project.id, error);
      return null;
    }
  }, [project.description]);

  const repositoryUrl = useMemo(() => {
    try {
      return getRepositoryUrl(project);
    } catch (error) {
      console.warn('Failed to get repository URL for project:', project.id, error);
      return project.web_url;
    }
  }, [project]);

  // If repository URL is invalid or missing, render as plain text
  if (!isValidExternalUrl(repositoryUrl)) {
    return (
      <span className="text-gray-700 truncate" title={projectPath}>
        {projectPath}
      </span>
    );
  }

  // Determine platform name and display path
  const isGitHub = githubUrl !== null && githubUrl !== '';
  const platformName = isGitHub ? 'GitHub' : 'DataOps.live';
  const displayPath = isGitHub ? githubUrl.replace('https://github.com/', '') : projectPath;

  // Render as accessible external link
  return (
    <a
      href={repositoryUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 hover:text-blue-800 hover:underline truncate transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-sm"
      title={`Open ${displayPath} in ${platformName} (opens in new tab)`}
      aria-label={`Open project ${displayPath} in ${platformName} in a new tab`}
      onClick={(e) => {
        // Prevent event propagation to avoid triggering parent project card navigation
        e.stopPropagation();
      }}
    >
      {displayPath}
    </a>
  );
};

export default function ProjectSearchView(props: {}): JSX.Element {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [groupSearchQuery, setGroupSearchQuery] = useState<string>('');
  const [dropdownProjectId, setDropdownProjectId] = useState<number | null>(null);

  const [selectedGroup, setSelectedGroup] = useState<IOption>({ name: '', id: 0, path: '' });
  const [filteredProjects, setFilteredProjects] = useState<IPlatformProject[]>([]);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: projects, isLoading } = useGetProjectsQuery({ search: searchQuery });
  const { data: groups } = useGetGroupsQuery({ search: groupSearchQuery });
  const { data: projectsFromGroup, isLoading: isLoadingProjectsFromGroup } = useGetProjectsFromGroupQuery(
    { groupId: selectedGroup.id, search: searchQuery },
    { skip: selectedGroup.id === 0 },
  );

  const navigate = useNavigate();
  const [forceSync, { isLoading: isForceSyncLoading }] = useForceSyncMutation();

  // Memoize filtered projects computation for better performance
  const memoizedFilteredProjects = useMemo(() => {
    if (projects !== undefined && selectedGroup.id !== 0) {
      return projectsFromGroup ?? [];
    }
    return projects ?? [];
  }, [projects, selectedGroup.id, projectsFromGroup]);

  // Use memoized projects instead of useEffect for better performance
  useEffect(() => {
    setFilteredProjects(memoizedFilteredProjects);
  }, [memoizedFilteredProjects]);

  // Memoize navigation handler to prevent unnecessary re-renders
  const handleProjectClick = useCallback(
    (projectId: number) => {
      navigate(`/astra-zeneca-monitoring/status/${projectId}`);
    },
    [navigate],
  );

  // Handler functions for dropdown
  const handleToggleDropdown = useCallback(
    (projectId: number) => {
      setDropdownProjectId(dropdownProjectId === projectId ? null : projectId);
    },
    [dropdownProjectId],
  );

  const handleCloseDropdown = useCallback(() => {
    setDropdownProjectId(null);
  }, []);

  const handleForceSync = useCallback(
    async (projectId: number, projectName: string) => {
      try {
        console.log('Triggering sync for project:', projectName);
        await forceSync({ projectId }).unwrap();
        console.log('Force sync completed successfully for project:', projectName);
        handleCloseDropdown();
      } catch (error) {
        console.error('Failed to force sync project:', projectName, error);
        // You might want to show a toast notification here
        handleCloseDropdown();
      }
    },
    [forceSync, handleCloseDropdown],
  );

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current != null && !dropdownRef.current.contains(event.target as Node)) {
        handleCloseDropdown();
      }
    };

    if (dropdownProjectId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [dropdownProjectId, handleCloseDropdown]);

  return (
    <>
      {/* <div className="w-full max-w-[30rem] relative mt-2 rounded-md shadow-sm">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon aria-hidden="true" className="h-5 w-5 text-gray-400" />
        </div>
        <input
          id="search"
          name="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search projects"
          className="w-full block rounded-md border-0 py-2.5 pl-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
      </div> */}
      <div className="w-full max-w-[70rem] flex flex-row gap-2">
        <div className="h-[33px] relative flex flex-1 rounded-md">
          <MagnifyingGlassIcon
            className="pointer-events-none absolute left-3 top-2 h-5 w-5 text-gray-500"
            aria-hidden="true"
          />
          <input
            className="h-[33px] w-2/3 bg-white border border-gray-200 bg-transparent pl-11 pr-4 text-gray-900 placeholder:text-gray-500 ring-0 focus:ring-0 sm:text-sm rounded-md outline-none outline-offset-0 focus:outline focus:outline-dataops-primary-light-blue/20 focus:outline-4 transition-all ease-in-out"
            data-testid="search-input"
            placeholder="Search projects"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="w-1/3">
            <ComboboxAutocomplete
              label=""
              options={(groups as IOption[]) ?? []}
              selected={selectedGroup}
              setSelected={(group: IOption) => setSelectedGroup(group)}
              query={groupSearchQuery}
              setQuery={setGroupSearchQuery}
              hideLabel={true}
              placeholder="Filter by group"
              showClearButton={true}
            />
          </div>
        </div>
      </div>
      <div className="w-full max-w-[70rem]">
        {filteredProjects !== undefined && filteredProjects.length === 20 && (
          <div className="mt-8 mr-[auto] mb-2 text-sm text-gray-600">Showing the first 20 projects</div>
        )}
        {filteredProjects !== undefined && filteredProjects.length < 20 && filteredProjects.length > 0 && (
          <div className="mt-8 mr-[auto] mb-2 text-sm text-gray-600">{filteredProjects.length} projects</div>
        )}
        {filteredProjects !== undefined && filteredProjects.length === 0 && (
          <div className="mt-8 mr-[auto] mb-2 text-sm text-gray-600">No projects found</div>
        )}
      </div>
      <ul role="list" className="w-full max-w-[70rem] mx-[auto] grid grid-cols-1 gap-x-4 gap-y-4 lg:grid-cols-3 pb-8">
        {filteredProjects?.map((project) => {
          // Simple GitHub detection without hooks inside map
          const metadata = parseRepositoryMetadataFromDescription(project.description);
          const githubUrl = metadata.githubUrl;
          const isGitHub = githubUrl !== null;
          const lastSynced = metadata.lastSynced;

          // Extract and generate the develop URL
          const dataopsEnvironment = process.env.REACT_APP_ENV;
          const gtDevelopUrl = (): string => {
            const baseUrl = `https://develop.${
              dataopsEnvironment?.startsWith('qa') ? 'qa.' : ''
            }dataops.live/new?orgSlug=astrazeneca&autostart=true`;

            const fragment = isGitHub ? githubUrl : `${project.web_url}/-/tree/${project.default_branch}/`;

            return `${baseUrl}#${fragment}`;
          };

          // Generate pipelines URL
          const pipelinesUrl = `${project.web_url}/-/pipelines`;

          // Copy link to clipboard helper
          const copyToClipboard = async (text: string, label: string): Promise<void> => {
            try {
              await navigator.clipboard.writeText(text);
              console.log(`${label} copied to clipboard`);
              // You could add a toast notification here
            } catch (error) {
              console.error(`Failed to copy ${label}:`, error);
            }
          };

          return (
            <li
              key={project.id}
              onClick={() => handleProjectClick(project.id)}
              className="cursor-pointer rounded-xl border border-gray-300 bg-white hover:border-gray-400 hover:shadow-md"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleProjectClick(project.id);
                }
              }}
              aria-label={`View details for project ${project.name}`}
            >
              <div className="flex items-center justify-between border-b border-gray-900/5 p-3">
                <div className="flex items-center gap-x-2 min-w-0 flex-1">
                  <div
                    className={classNames(
                      project.avatar_url !== null
                        ? 'bg-white border border-gray-300'
                        : isGitHub
                        ? 'bg-gray-900'
                        : 'bg-dataops-primary-blue',
                      'w-6 h-6 rounded-full object-contain overflow-hidden flex items-center justify-center flex-shrink-0',
                    )}
                  >
                    {project.avatar_url !== null ? (
                      <img className="p-0.5" src={project.avatar_url} alt={`${project.name} avatar`} />
                    ) : isGitHub ? (
                      <img
                        className="w-full h-full p-0.5 filter brightness-0 invert"
                        src="/static/github-mark.svg"
                        alt="GitHub project"
                        aria-label={`${project.name} is a GitHub project`}
                      />
                    ) : (
                      <div className="rounded-full text-white font-medium">{project.name.charAt(0).toUpperCase()}</div>
                    )}
                  </div>
                  <div className="text-base font-medium leading-6 text-gray-900 truncate">{project.name}</div>
                </div>

                {/* Action buttons section */}
                <div className="flex items-center gap-x-1 flex-shrink-0">
                  <a
                    href={`${project.web_url}/-/pipelines`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors duration-150 shadow-sm"
                    title="View pipelines"
                  >
                    Pipelines
                  </a>

                  <a
                    href={gtDevelopUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors duration-150 shadow-sm"
                    title="Open in Develop environment"
                  >
                    Develop
                  </a>

                  <div className="relative" ref={dropdownProjectId === project.id ? dropdownRef : null}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleDropdown(project.id);
                      }}
                      className="p-1 text-gray-600 bg-gray-100 border border-gray-300 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors duration-150 shadow-sm"
                      title="More options"
                    >
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </button>

                    {/* Dropdown Menu */}
                    {dropdownProjectId === project.id && (
                      <div className="absolute right-0 mt-1 w-52 bg-white border border-gray-300 rounded-md shadow-lg z-50">
                        <div className="py-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void copyToClipboard(pipelinesUrl, 'Pipelines link');
                              handleCloseDropdown();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                          >
                            Copy link to Pipelines
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void copyToClipboard(gtDevelopUrl(), 'Develop link');
                              handleCloseDropdown();
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                          >
                            Copy link to Develop
                          </button>
                          {isGitHub && (
                            <>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleForceSync(project.id, project.name);
                                }}
                                disabled={isForceSyncLoading}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                              >
                                {isForceSyncLoading ? 'Syncing...' : 'Force Sync Now'}
                              </button>
                              <div className="px-4 py-2 text-xs text-gray-500">
                                {(() => {
                                  if (lastSynced === null) {
                                    return 'Last Synced: Never';
                                  }
                                  const relativeTime = formatRelativeTime(lastSynced);
                                  return relativeTime !== null && relativeTime !== ''
                                    ? `Last Synced: ${relativeTime}`
                                    : 'Last Synced: Never';
                                })()}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <dl className="divide-y divide-gray-100 px-3 py-1.5 text-sm leading-6">
                <div className="flex justify-between gap-x-4 py-1">
                  <dt className="text-gray-500 whitespace-nowrap">Project path</dt>
                  <dd className="text-gray-700 truncate">
                    <ProjectPathLink project={project} />
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-1">
                  <dt className="text-gray-500 whitespace-nowrap">Last modified</dt>
                  <dd className="text-gray-700 truncate">
                    <time dateTime="2024-10-10">
                      {`${new Date(project.last_activity_at).toLocaleString('en-US', { month: 'short' })} ${new Date(
                        project.last_activity_at,
                      ).toLocaleString('en-US', { day: 'numeric' })}, ${new Date(
                        project.last_activity_at,
                      ).getFullYear()}`}
                    </time>
                  </dd>
                </div>
              </dl>
            </li>
          );
        })}
      </ul>
      {(isLoading || isLoadingProjectsFromGroup) && (
        <div className="min-h-8 flex items-center justify-center">
          <div
            className="animate-spin inline-block w-8 h-8 border-[2px] border-current border-t-transparent text-dataops-primary rounded-full"
            role="status"
            aria-label="loading"
          >
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      )}
    </>
  );
}
